/**
 * ScreenshotAttachment — file picker for optional screenshot upload.
 *
 * Track 4.Q — in-app feedback pipeline.
 * Feature plan: .plan/features/2026-04-24-admin-beta-feedback.md §2
 *
 * Flow:
 *   1. User picks a file (PNG, JPEG, WEBP) via the file input.
 *   2. Component calls feedback.presignScreenshotUpload to get a presigned
 *      PUT URL and an opaque R2 key.
 *   3. Component PUTs the file directly to R2 via the signed URL.
 *   4. On success, calls onAttach(r2Key) so FeedbackModal can include the
 *      key in the feedback.submit payload.
 *
 * Security note: the R2 key is opaque ("feedback/<uuid>.png") — it is never
 * rendered as a URL in the UI. The issue body will contain the raw key only
 * so the admin can retrieve it via the admin panel (architecture.md §4.8).
 *
 * Error copy is voice-compliant (branding.md §5):
 *   - No exclamation marks
 *   - No `we`/`us`/`our`
 *   - No banned words (branding.md §5.2.1)
 */

import { useState, useRef, type ChangeEvent } from "react";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ScreenshotAttachmentProps {
  /**
   * Called with the R2 key once the screenshot has been uploaded successfully.
   * Pass null to clear the current attachment.
   */
  onAttach: (r2Key: string | null) => void;

  /** Whether an attachment is currently in progress (disables the control). */
  disabled?: boolean;

  /**
   * RPC caller — returns { putUrl, r2Key }. Expected to call
   * feedback.presignScreenshotUpload on the server.
   * Decoupled so tests can supply a stub.
   */
  onRequestPresignUrl: () => Promise<{ putUrl: string; r2Key: string }>;
}

type AttachState = "idle" | "uploading" | "attached" | "error";

// ── Component ─────────────────────────────────────────────────────────────────

export function ScreenshotAttachment({
  onAttach,
  disabled = false,
  onRequestPresignUrl,
}: ScreenshotAttachmentProps) {
  const [state, setState] = useState<AttachState>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side type check — accept PNG, JPEG, WEBP only.
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setState("error");
      setErrorMsg("File type not supported. Use PNG, JPEG, or WebP.");
      return;
    }

    setState("uploading");
    setErrorMsg(null);
    setFileName(file.name);

    let presign: { putUrl: string; r2Key: string };
    try {
      presign = await onRequestPresignUrl();
    } catch {
      setState("error");
      setErrorMsg("Could not start upload. Try again.");
      return;
    }

    try {
      const res = await fetch(presign.putUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });
      if (!res.ok) {
        throw new Error(`Upload returned ${res.status}`);
      }
    } catch {
      setState("error");
      setErrorMsg("Upload failed. Try again.");
      return;
    }

    setState("attached");
    onAttach(presign.r2Key);
  }

  function handleRemove() {
    setState("idle");
    setFileName(null);
    setErrorMsg(null);
    onAttach(null);
    // Reset the file input so the same file can be re-selected.
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  return (
    <div className="proof-feedback-screenshot" data-testid="screenshot-attachment">
      {state === "idle" || state === "error" ? (
        <label
          className="proof-feedback-screenshot__label"
          style={{ cursor: disabled ? "default" : "pointer" }}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            disabled={disabled}
            onChange={(e) => void handleFileChange(e)}
            data-testid="screenshot-file-input"
          />
          <span className="proof-feedback-screenshot__btn" aria-disabled={disabled}>
            Attach screenshot
          </span>
        </label>
      ) : state === "uploading" ? (
        <span className="proof-feedback-screenshot__uploading" aria-live="polite">
          Uploading…
        </span>
      ) : (
        /* attached */
        <div className="proof-feedback-screenshot__attached" aria-live="polite">
          <span className="proof-feedback-screenshot__filename" title={fileName ?? ""}>
            {fileName}
          </span>
          <button
            type="button"
            className="proof-feedback-screenshot__remove"
            onClick={handleRemove}
            aria-label="Remove screenshot"
            data-testid="screenshot-remove-btn"
          >
            Remove
          </button>
        </div>
      )}

      {errorMsg !== null && (
        <p className="proof-feedback-screenshot__error" role="alert" data-testid="screenshot-error">
          {errorMsg}
        </p>
      )}
    </div>
  );
}
